import { CSSObject } from '@emotion/react';
import { theme } from 'styles';
import { desktop, mobile } from 'styles/media';
import * as zIndex from 'styles/zIndex';

const { color, radius } = theme;

const nav = {
  bg: color.primary,
  text: 'rgba(255, 255, 255, 0.6)',
  textActive: color.white,
  textSub: 'rgba(255, 255, 255, 0.5)',
  muted: 'rgba(255, 255, 255, 0.35)',
  hover: 'rgba(255, 255, 255, 0.08)',
  selected: 'rgba(255, 255, 255, 0.12)',
  divider: 'rgba(255, 255, 255, 0.08)',
} as const;

const SIDE_DURATION = 0.25;
const OPEN_WIDTH = 248;
const CLOSED_WIDTH = 76;
const DRAWER_WIDTH = 288;

const wrap = (isOpen: boolean, isDrawerOpen: boolean): CSSObject => ({
  position: 'relative',
  display: 'flex',
  flex: 'none',
  flexDirection: 'column',
  width: isOpen ? OPEN_WIDTH : CLOSED_WIDTH,
  height: '100%',
  background: nav.bg,
  color: nav.text,
  transition: `width ${SIDE_DURATION}s ease`,
  overflow: 'hidden',

  [mobile]: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: zIndex.DRAWER + 1,
    width: DRAWER_WIDTH,
    maxWidth: '85vw',
    borderRadius: `0 ${radius.xl}px ${radius.xl}px 0`,
    boxShadow: isDrawerOpen ? theme.shadow.modal : 'none',
    transform: `translateX(${isDrawerOpen ? 0 : '-100%'})`,
    transition: `transform ${SIDE_DURATION}s ease`,
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
});

const overlay = (isDrawerOpen: boolean): CSSObject => ({
  display: 'none',
  [mobile]: {
    display: 'block',
    position: 'fixed',
    inset: 0,
    zIndex: zIndex.DRAWER,
    background: 'rgba(0, 0, 0, 0.4)',
    opacity: isDrawerOpen ? 1 : 0,
    pointerEvents: isDrawerOpen ? 'auto' : 'none',
    transition: `opacity ${SIDE_DURATION}s ease`,
  },
});

const top = (isOpen: boolean): CSSObject => ({
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: isOpen ? 'space-between' : 'center',
  height: 64,
  padding: isOpen ? '0 12px 0 24px' : 0,
  [mobile]: {
    justifyContent: 'space-between',
    padding: '0 8px 0 20px',
    height: 60,
  },
});

const logo = (isOpen: boolean): CSSObject => ({
  display: isOpen ? 'flex' : 'none',
  alignItems: 'center',
  '> img': {
    height: 22,
    width: 'auto',
  },
  [mobile]: {
    display: 'flex',
  },
});

const iconButton: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: radius.md,
  color: nav.text,
  transition: 'background-color 0.15s ease',
  ':hover': {
    background: nav.hover,
    color: nav.textActive,
  },
};

const toggle: CSSObject = {
  ...iconButton,
  [mobile]: {
    display: 'none',
  },
};

const closeButton: CSSObject = {
  ...iconButton,
  width: 44,
  height: 44,
  [desktop]: {
    display: 'none',
  },
};

const contentsWrap: CSSObject = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
};

const menuWrap: CSSObject = {
  padding: '8px 12px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const menuGroup = (isCurrent: boolean, isCollapsed: boolean): CSSObject => ({
  display: 'flex',
  justifyContent: isCollapsed ? 'center' : 'space-between',
  alignItems: 'center',
  height: 44,
  padding: isCollapsed ? 0 : '0 12px',
  borderRadius: radius.md,
  userSelect: 'none',
  cursor: 'pointer',
  color: isCurrent ? nav.textActive : nav.text,
  background: isCollapsed && isCurrent ? nav.selected : 'transparent',
  transition: 'background-color 0.15s ease',

  '> div': {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 15,
    fontWeight: isCurrent ? 700 : 500,
    whiteSpace: 'nowrap',
    '> span': {
      display: isCollapsed ? 'none' : 'inline',
    },
  },
  '> svg': {
    display: isCollapsed ? 'none' : 'block',
    color: nav.muted,
  },
  ':hover': {
    background: nav.hover,
    color: nav.textActive,
  },

  [mobile]: {
    justifyContent: 'space-between',
    padding: '0 12px',
    height: 48,
    background: 'transparent',
    '> div > span': { display: 'inline' },
    '> svg': { display: 'block' },
  },
});

const menuArrow = (isOpen: boolean): CSSObject => ({
  flex: 'none',
  transform: `rotate(${isOpen ? -180 : 0}deg)`,
  transition: 'transform 0.25s ease',
});

const subMenuWrap = (isOpen: boolean): CSSObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  maxHeight: isOpen ? '100vh' : 0,
  transition: 'max-height 0.3s ease',
  overflow: 'hidden',
  paddingBottom: isOpen ? 6 : 0,
});

const subMenu = (isSelected: boolean): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  height: 40,
  padding: '0 12px 0 44px',
  borderRadius: radius.md,
  fontSize: 14,
  fontWeight: isSelected ? 700 : 400,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  color: isSelected ? nav.textActive : nav.textSub,
  background: isSelected ? nav.selected : 'transparent',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  ':hover': {
    cursor: 'pointer',
    color: nav.textActive,
    background: nav.hover,
  },
  [mobile]: {
    height: 44,
  },
});

const profileWrap = (isCollapsed: boolean): CSSObject => ({
  flex: 'none',
  display: 'flex',
  flexDirection: isCollapsed ? 'column' : 'row',
  alignItems: 'center',
  gap: isCollapsed ? 4 : 8,
  margin: '0 12px',
  padding: '12px 0 16px',
  borderTop: `1px solid ${nav.divider}`,
  [mobile]: {
    flexDirection: 'row',
    gap: 8,
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  },
});

const profile = (isCollapsed: boolean): CSSObject => ({
  flex: isCollapsed ? 'none' : 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: isCollapsed ? '6px 0' : '6px 4px',
  [mobile]: {
    flex: 1,
    padding: '6px 4px',
  },
});

const avatar: CSSObject = {
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: color.white,
  color: color.primary,
  fontSize: 13,
  fontWeight: 700,
};

const profileText = (isCollapsed: boolean): CSSObject => ({
  minWidth: 0,
  display: isCollapsed ? 'none' : 'flex',
  flexDirection: 'column',
  gap: 3,
  strong: {
    fontSize: 13,
    fontWeight: 600,
    color: nav.textActive,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  small: {
    fontSize: 11,
    color: nav.textSub,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [mobile]: {
    display: 'flex',
  },
});

const logoutButton = (isCollapsed: boolean): CSSObject => ({
  ...iconButton,
  flex: 'none',
  width: 'auto',
  gap: 6,
  padding: isCollapsed ? 0 : '0 10px',
  minWidth: 40,
  fontSize: 13,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  '> span': {
    display: isCollapsed ? 'none' : 'inline',
  },
  [mobile]: {
    padding: '0 12px',
    height: 44,
    '> span': { display: 'inline' },
  },
});

const menuIcon = (icon: string): CSSObject => ({
  marginRight: '4px',
  width: '22px',
  height: '22px',
  background: `url(${icon}) center center no-repeat`,
});

export {
  wrap,
  overlay,
  top,
  logo,
  toggle,
  closeButton,
  contentsWrap,
  menuWrap,
  menuGroup,
  menuIcon,
  menuArrow,
  subMenu,
  subMenuWrap,
  profileWrap,
  profile,
  avatar,
  profileText,
  logoutButton,
};
